/*
  HOK product page — UI layer for sections/hok-pdp.liquid.

  Product logic is NOT duplicated here. Variant selection, price text, SKU, gallery, ?variant= and the
  Add to Cart request keep running through the theme's own code (product-media.js, application.js,
  Splide, Swiper, share.js, the review and wishlist apps). This file only adds:
    - smooth scroll from the top rating / "View Similar" to their sections (sticky-header aware)
    - desktop description tabs + Read More / Read Less, mobile accordions
    - reviews expand / collapse and the "Write Review" shortcut into the review app
    - variant sync for the extra price displays (sticky card, mobile bar, shade preview, "X left")
    - desktop sticky card and mobile bar buttons that click the theme's own #main-product-handle-btn
    - mobile purchase bar price block (IntersectionObserver on the main price; the bar itself is always on)
    - Customers Also Viewed slider (theme's Swiper) incl. async Shopify recommendations
  Safe to load twice and to re-init on Theme Editor section reloads.
*/
(function () {
  'use strict';

  if (window.HokPDP) {
    window.HokPDP.initAll(document);
    return;
  }

  var DESKTOP = window.matchMedia('(min-width: 1024px)');
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(null, args);
      }, wait);
    };
  }

  function unit() {
    /* mirrors the CSS units: --u (desktop, 1440 frame) and --m (mobile, 402 frame) */
    var w = window.innerWidth;
    if (DESKTOP.matches) return Math.min(1, Math.max(0.72, w / 1440));
    if (w >= 600) return 1.2;
    return Math.min(1.2, Math.max(0.88, w / 402));
  }

  function scrollBehavior() {
    return REDUCED.matches ? 'auto' : 'smooth';
  }

  /* ---------------------------------------------------------------- sticky header offset */
  function headerHeight() {
    var bottom = 0;
    var candidates = document.querySelectorAll('.shopify-section-group-header-group, #shopify-section-header');
    Array.prototype.forEach.call(candidates, function (el) {
      var style = getComputedStyle(el);
      if (style.position !== 'sticky' && style.position !== 'fixed') return;
      var rect = el.getBoundingClientRect();
      if (rect.height === 0 || rect.top > 1 || rect.height > window.innerHeight * 0.5) return;
      bottom = Math.max(bottom, rect.bottom);
    });
    return Math.round(bottom);
  }

  function setOffsets(root) {
    root.style.setProperty('--hpdp-header-h', headerHeight() + 'px');
    var nav = document.querySelector('.bottom-nav');
    var navHeight = 0;
    if (nav && getComputedStyle(nav).position === 'fixed' && nav.offsetParent !== null) {
      navHeight = Math.round(nav.getBoundingClientRect().height);
    }
    root.style.setProperty('--hpdp-bottom-offset', navHeight + 'px');
  }

  function scrollToTarget(root, target) {
    if (!target) return;
    setOffsets(root);
    var top = target.getBoundingClientRect().top + window.pageYOffset - headerHeight() - 16;
    window.scrollTo({ top: Math.max(0, top), behavior: scrollBehavior() });
    if (target.hasAttribute('tabindex')) target.focus({ preventScroll: true });
  }

  /* ---------------------------------------------------------------- variant data */
  function variantData(root) {
    if (root._hpdpVariants) return root._hpdpVariants;
    var node = root.querySelector('[data-hpdp-variants]');
    var data = {};
    if (node) {
      try {
        data = JSON.parse(node.textContent) || {};
      } catch (error) {
        data = {};
      }
    }
    root._hpdpVariants = data;
    return data;
  }

  function setText(scope, selector, text) {
    scope.querySelectorAll(selector).forEach(function (node) {
      node.textContent = text;
    });
  }

  /* Low stock: "N Left" below the whole variant selector (never inside a tile). Same rule as the server render:
     tracked inventory + deny policy + available + 0 < qty <= threshold. */
  function updateStock(root, input, variant) {
    var stock = root.querySelector('[data-hpdp-stock]');
    if (!stock) return;
    var threshold = parseInt(root.dataset.stockThreshold, 10) || 0;
    var qty = variant ? parseInt(variant.inventory, 10) : 0;
    var show = !!(variant && variant.tracked && variant.available && qty > 0 && qty <= threshold);
    stock.hidden = !show;
    stock.textContent = show ? qty + ' Left' : '';
  }

  /* ONE source of truth for the selected variant: the checked swatch radio.
     Every consumer (theme ATC button, buy-buttons form input[name="id"] read by Shiprocket + Shopify
     dynamic checkout, wishlist, sticky bar) is written from it synchronously, before any click can happen. */
  function selectedInput(root) {
    return root.querySelector('.swatch-element input[type="radio"]:checked');
  }

  function syncVariantId(root, input) {
    if (!input) return;
    var id = String(input.value);
    var variant = variantData(root)[id];
    var available = variant ? !!variant.available : input.getAttribute('is_variant_available') === 'true';

    root.querySelectorAll('form input[name="id"]').forEach(function (field) {
      field.value = id;
      field.disabled = !available;
    });

    var atc = root.querySelector('#main-product-handle-btn');
    if (atc) {
      atc.value = id;
      atc.setAttribute('product_variantid', id);
    }
    root.querySelectorAll('.wishlist-engine[data-variant_id]').forEach(function (node) {
      node.setAttribute('data-variant_id', id);
    });

    root.querySelectorAll('[data-hpdp-buy-now]').forEach(function (wrap) {
      wrap.classList.toggle('is-disabled', !available);
      wrap.setAttribute('aria-disabled', available ? 'false' : 'true');
    });

    root.querySelectorAll('.swatch-element').forEach(function (element) {
      var radio = element.querySelector('input[type="radio"]');
      element.classList.toggle('is-selected', radio === input);
    });

  }

  /* Sticky side card shade dots — DISPLAY ONLY (theme card look: selected shade first, then two others, +N).
     Rebuilt from the one selected variant whenever the main selector changes. */
  function renderCardDots(root, selectedId) {
    var wrap = root.querySelector('[data-hpdp-card-dots]');
    if (!wrap) return;
    var data = variantData(root);
    var ids = Object.keys(data);
    var order = [selectedId].concat(ids.filter(function (id) { return id !== selectedId; })).slice(0, 3);
    var dots = wrap.querySelectorAll('[data-hpdp-card-dot]');
    order.forEach(function (id, index) {
      var dot = dots[index];
      var v = data[id];
      if (!dot || !v) return;
      dot.classList.toggle('available', !!v.available);
      dot.classList.toggle('b-swatch_colors-item', !v.available);
      dot.classList.toggle('m-disabled', !v.available);
      if (v.color) {
        dot.style.backgroundImage = '';
        dot.style.setProperty('background-color', v.color, 'important');
      } else {
        dot.style.removeProperty('background-color');
        dot.style.backgroundImage = v.swatch ? 'url("' + v.swatch + '")' : '';
      }
      dot.title = v.name || '';
    });
  }

  function initBuyNow(root, signal) {
    var wraps = root.querySelectorAll('[data-hpdp-buy-now]');
    if (!wraps.length) return;
    wraps.forEach(function (wrap) {
      /* Last-moment guard: re-sync from the checked radio, and never let a sold-out variant through. */
      wrap.addEventListener(
        'click',
        function (event) {
          syncVariantId(root, selectedInput(root));
          if (wrap.classList.contains('is-disabled')) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        },
        { capture: true, signal: signal }
      );
      /* Shiprocket injects its button after load; when it is present the Shopify payment button stays hidden
         (same rule as theme.liquid renderButton), so only one Buy Now is ever shown. */
      var detect = function () {
        var host = wrap.querySelector('.shiprocket-headless');
        var sr = wrap.querySelector('.sr-headless-checkout');
        var active = !!(host && sr && getComputedStyle(host).display !== 'none');
        if (wrap.classList.contains('hpdp-buy-now--sr') !== active) wrap.classList.toggle('hpdp-buy-now--sr', active);
      };
      detect();
      var observer = new MutationObserver(detect);
      observer.observe(wrap, { childList: true, subtree: true });
      window.addEventListener('load', detect, { signal: signal });
      root._hpdpObservers.push(observer);
    });
  }

  function applyVariant(root, input) {
    var variant = variantData(root)[input.value];
    if (!variant) return;
    var onSale = !!variant.compare;

    setText(root, '[data-hpdp-variant-name]', variant.name);

    /* main price block — product-media.js already wrote price / compare / discount; normalise the format */
    var price = root.querySelector('[data-hpdp-price]');
    if (price) {
      price.classList.toggle('is-on-sale', onSale);
      setText(price, '.discount_label', onSale ? variant.discount + '%' : '');
      if (!onSale) setText(price, '.rack-rate', '');
    }

    setText(root, '[data-hpdp-current]', variant.price);
    setText(root, '[data-hpdp-compare]', variant.compare);
    setText(root, '[data-hpdp-discount]', onSale ? variant.discount + '%' : '');
    setText(root, '[data-hpdp-savings]', variant.savings);
    setText(root, '[data-hpdp-card-off]', onSale ? variant.discount + '% Off' : '');

    /* sticky card: price-drop pill (snippets/hok-price-drop uses the hidden attribute) */
    root.querySelectorAll('[data-hpdp-card-dropped]').forEach(function (node) {
      node.hidden = !onSale;
    });
    renderCardDots(root, input.value);
    /* product-media.js may have rebuilt the gallery slider for this variant */
    if (root._hpdpAttachGalleryDots) setTimeout(root._hpdpAttachGalleryDots, 60);

    ['[data-hpdp-dropped]', '[data-hpdp-bar-tab]', '[data-hpdp-card-dropped]', '[data-hpdp-card-off]', '[data-hpdp-card-compare]'].forEach(function (selector) {
      root.querySelectorAll(selector).forEach(function (node) {
        node.classList.toggle('is-hidden', !onSale);
      });
    });
    root.querySelectorAll('[data-hpdp-bar-price]').forEach(function (node) {
      node.classList.toggle('is-on-sale', onSale);
    });

    var preview = root.querySelector('[data-hpdp-preview]');
    if (preview) {
      preview.style.backgroundColor = variant.color || '';
      preview.style.backgroundImage = !variant.color && variant.swatch ? 'url("' + variant.swatch + '")' : '';
    }

    var cardImage = root.querySelector('[data-hpdp-card-image]');
    if (cardImage && variant.image) {
      cardImage.removeAttribute('srcset');
      cardImage.src = variant.image;
    }

    updateStock(root, input, variant);
  }

  function initVariantSync(root, signal) {
    document.addEventListener(
      'change',
      function (event) {
        var input = event.target.closest && event.target.closest('.swatch-element input[type="radio"]');
        if (!input || !root.contains(input)) return;
        /* ids first, synchronously — Buy Now can never read a stale variant */
        syncVariantId(root, input);
        updateStock(root, input, variantData(root)[input.value]);
        /* display updates after the theme's own change handlers (product-media.js) */
        setTimeout(function () {
          applyVariant(root, input);
          syncVariantId(root, selectedInput(root));
        }, 0);
      },
      { signal: signal }
    );
    var checked = selectedInput(root);
    if (checked) {
      var variant = variantData(root)[checked.value];
      syncVariantId(root, checked);
      if (variant) {
        setText(root, '[data-hpdp-variant-name]', variant.name);
        updateStock(root, checked, variant);
      }
    }
  }

  /* ---------------------------------------------------------------- buttons that reuse the theme's Add to Cart */
  function initProxyButtons(root, signal) {
    var main = root.querySelector('#main-product-handle-btn');
    var proxies = root.querySelectorAll('[data-hpdp-proxy-atc]');
    if (!proxies.length) return;
    if (!main) {
      proxies.forEach(function (button) {
        button.hidden = true;
      });
      return;
    }
    var mirror = function () {
      var label = (main.textContent || '').trim();
      proxies.forEach(function (button) {
        button.disabled = main.disabled;
        if (main.disabled) {
          button.textContent = label || 'Out of Stock';
        } else if (/adding/i.test(label)) {
          button.textContent = 'Adding…';
        } else {
          button.textContent = button.dataset.label || label;
        }
      });
    };
    mirror();
    proxies.forEach(function (button) {
      button.addEventListener(
        'click',
        function () {
          if (!main.disabled) main.click();
        },
        { signal: signal }
      );
    });
    var observer = new MutationObserver(mirror);
    observer.observe(main, { attributes: true, attributeFilter: ['disabled'], childList: true, characterData: true, subtree: true });
    root._hpdpObservers.push(observer);
  }

  /* ---------------------------------------------------------------- mobile purchase bar */
  function initMobileBar(root) {
    var bar = root.querySelector('[data-hpdp-bar]');
    var price = root.querySelector('[data-hpdp-price]');
    if (!bar || !price || !root.classList.contains('hpdp--bar')) return;
    if (!('IntersectionObserver' in window)) {
      /* very old browsers: keep the Add to Cart reachable in the page flow instead */
      root.classList.remove('hpdp--bar');
      return;
    }

    /* Mobile states:
       1. the fixed Add to Cart bar itself — visible from page load, never scroll-triggered. CSS holds that
          default state (.hpdp--bar .hpdp-bar), so the bar is already in place before this script runs;
          .is-visible is kept in sync here only for anything else that reads it.
       2. bar.is-price-visible  — the price block inside the bar. Slides in from the left only while the main
                                   PDP price ([data-hpdp-price]) is above the viewport; slides out when it returns.
       The bar's price nodes are the existing [data-hpdp-current] / [data-hpdp-compare] / [data-hpdp-discount]
       targets, so variant changes keep updating them. */
    var barPrice = bar.querySelector('[data-hpdp-bar-price]');
    var pricePassed = false;
    /* The price block animates from width 0 to its own content width; measure that width (it changes with the
       variant price) so Add to Cart — flex: 1 — shrinks by exactly the room the price needs. */
    var measurePrice = function () {
      if (!barPrice) return;
      var width = Math.ceil(barPrice.scrollWidth);
      if (width > 0) bar.style.setProperty('--hpdp-bar-price-w', width + 'px');
    };
    if (barPrice) {
      var priceWatcher = new MutationObserver(measurePrice);
      priceWatcher.observe(barPrice, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['class'] });
      root._hpdpObservers.push(priceWatcher);
    }
    var render = function () {
      var mobile = !DESKTOP.matches;
      measurePrice();
      /* the bar is on for the whole page on mobile; only the price block reacts to scrolling */
      var showPrice = mobile && pricePassed;
      bar.classList.toggle('is-visible', mobile);
      bar.classList.toggle('is-price-visible', showPrice);
      bar.removeAttribute('inert');
      bar.removeAttribute('aria-hidden');
      if (barPrice) {
        if (mobile && !showPrice) barPrice.setAttribute('aria-hidden', 'true');
        else barPrice.removeAttribute('aria-hidden');
      }
    };

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          /* the main price has scrolled above the viewport (not merely below it) */
          pricePassed = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        });
        render();
      },
      { threshold: 0 }
    );
    observer.observe(price);
    root._hpdpObservers.push(observer);
    root._hpdpBarRender = render;
    render();
  }

  /* ---------------------------------------------------------------- description: tabs (desktop) + accordion (mobile) */
  function selectTab(root, tab, focus) {
    var list = tab.closest('[role="tablist"]');
    list.querySelectorAll('[role="tab"]').forEach(function (item) {
      var selected = item === tab;
      item.setAttribute('aria-selected', selected ? 'true' : 'false');
      item.tabIndex = selected ? 0 : -1;
      var panel = document.getElementById(item.getAttribute('aria-controls'));
      if (panel) panel.classList.toggle('is-active', selected);
    });
    if (focus) tab.focus();
    var panel = document.getElementById(tab.getAttribute('aria-controls'));
    if (panel) measureReadMore(panel.querySelector('[data-hpdp-readmore]'));
  }

  function visibleTabs(root) {
    return Array.prototype.filter.call(root.querySelectorAll('.hpdp-tabs__tab'), function (tab) {
      return !tab.classList.contains('hpdp-hide-desktop');
    });
  }

  function setAccordion(panel, open) {
    var head = panel.querySelector('[data-hpdp-accordion]');
    panel.classList.toggle('is-open', open);
    if (head) head.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function initTabs(root, signal) {
    var tabs = visibleTabs(root);
    if (tabs.length) selectTab(root, tabs[0], false);

    root.addEventListener(
      'click',
      function (event) {
        var tab = event.target.closest('.hpdp-tabs__tab');
        if (tab && root.contains(tab)) {
          selectTab(root, tab, false);
          return;
        }
        var head = event.target.closest('[data-hpdp-accordion]');
        if (head && root.contains(head)) {
          var panel = head.closest('[data-hpdp-panel]');
          setAccordion(panel, !panel.classList.contains('is-open'));
        }
      },
      { signal: signal }
    );

    root.addEventListener(
      'keydown',
      function (event) {
        var tab = event.target.closest('.hpdp-tabs__tab');
        if (!tab || !root.contains(tab)) return;
        var list = visibleTabs(root);
        var index = list.indexOf(tab);
        var next = null;
        if (event.key === 'ArrowRight') next = list[(index + 1) % list.length];
        if (event.key === 'ArrowLeft') next = list[(index - 1 + list.length) % list.length];
        if (event.key === 'Home') next = list[0];
        if (event.key === 'End') next = list[list.length - 1];
        if (next) {
          event.preventDefault();
          selectTab(root, next, true);
        }
      },
      { signal: signal }
    );
  }

  /* ---------------------------------------------------------------- Read More / Read Less (desktop) */
  function measureReadMore(wrapper) {
    if (!wrapper) return;
    var content = wrapper.querySelector('[data-hpdp-readmore-content]');
    var toggle = wrapper.querySelector('[data-hpdp-readmore-toggle]');
    if (!content || !toggle) return;
    if (wrapper.dataset.expanded === 'true') return;
    if (!DESKTOP.matches || content.offsetParent === null) {
      wrapper.classList.remove('is-collapsed', 'is-collapsible');
      toggle.hidden = true;
      return;
    }
    content.style.maxHeight = '';
    wrapper.classList.add('is-collapsed');
    var overflowing = content.scrollHeight > content.clientHeight + 8;
    wrapper.classList.toggle('is-collapsed', overflowing);
    wrapper.classList.toggle('is-collapsible', overflowing);
    toggle.hidden = !overflowing;
  }

  function expandCollapse(wrapper, content, toggle, expand, collapsedClass) {
    var label = toggle.querySelectorAll('[data-hpdp-readmore-label], [data-hpdp-reviews-label]');
    if (expand) {
      content.style.maxHeight = content.clientHeight + 'px';
      wrapper.classList.remove(collapsedClass);
      if (collapsedClass === 'is-collapsed' && wrapper !== content) content.classList.remove(collapsedClass);
      wrapper.dataset.expanded = 'true';
      requestAnimationFrame(function () {
        content.style.maxHeight = content.scrollHeight + 'px';
      });
      var done = function () {
        if (wrapper.dataset.expanded === 'true') content.style.maxHeight = 'none';
        content.removeEventListener('transitionend', done);
      };
      content.addEventListener('transitionend', done);
      if (REDUCED.matches) done();
    } else {
      content.style.maxHeight = content.scrollHeight + 'px';
      content.offsetHeight; /* reflow so the transition runs */
      wrapper.dataset.expanded = 'false';
      wrapper.classList.add(collapsedClass);
      content.style.maxHeight = '';
      var rect = wrapper.getBoundingClientRect();
      if (rect.top < 0) {
        var card = wrapper.closest('.hpdp-card') || wrapper;
        window.scrollTo({ top: card.getBoundingClientRect().top + window.pageYOffset - headerHeight() - 16, behavior: scrollBehavior() });
      }
    }
    toggle.setAttribute('aria-expanded', expand ? 'true' : 'false');
    label.forEach(function (node) {
      if (node.hasAttribute('data-hpdp-readmore-label')) {
        node.textContent = expand ? toggle.dataset.less : toggle.dataset.more;
      } else {
        node.textContent = expand ? toggle.dataset.less : node.dataset.more;
      }
    });
  }

  function refreshReadMore(root) {
    root.querySelectorAll('[data-hpdp-readmore]').forEach(measureReadMore);
  }

  function initReadMore(root, signal) {
    root.addEventListener(
      'click',
      function (event) {
        var toggle = event.target.closest('[data-hpdp-readmore-toggle]');
        if (!toggle || !root.contains(toggle)) return;
        var wrapper = toggle.closest('[data-hpdp-readmore]');
        var content = wrapper.querySelector('[data-hpdp-readmore-content]');
        expandCollapse(wrapper, content, toggle, toggle.getAttribute('aria-expanded') !== 'true', 'is-collapsed');
      },
      { signal: signal }
    );
    root.addEventListener(
      'load',
      function (event) {
        if (event.target.tagName === 'IMG' && event.target.closest('[data-hpdp-readmore-content]')) refreshReadMore(root);
      },
      { capture: true, signal: signal }
    );
    refreshReadMore(root);
  }

  /* ---------------------------------------------------------------- reviews: expand / collapse, write review */
  /* ---------------------------------------------------------------- reviews
     Mobile: the collapsed state is built from whole review ITEMS (never a height clip): everything the review app
     renders before the list (summary, customer photos, sort) stays, review 1 is fully visible, reviews 2..n get
     .hpdp-review-hidden until "View all Reviews". The app's markup is not ours, so the list is found either with the
     selector set in the theme editor (Reviews block → "Review item selector") or automatically: the container whose
     children repeat the same tag/class and each carry review text. If no list is found, the previous max-height
     collapse is used as a fallback. Desktop keeps its existing max-height behaviour. */
  function reviewItems(root, body) {
    var custom = (body.getAttribute('data-item-selector') || '').trim();
    if (custom) {
      try {
        var matched = body.querySelectorAll(custom);
        if (matched.length) return Array.prototype.slice.call(matched);
      } catch (error) {
        /* invalid selector → automatic detection */
      }
    }
    var best = null;
    var bestScore = 0;
    body.querySelectorAll('*').forEach(function (parent) {
      if (parent.children.length < 2) return;
      var groups = {};
      Array.prototype.forEach.call(parent.children, function (child) {
        if (/^(SCRIPT|STYLE|TEMPLATE|svg|BUTTON|IMG|INPUT|SELECT|OPTION)$/i.test(child.tagName)) return;
        var key = child.tagName + '|' + (child.getAttribute('class') || '').replace(/\bhpdp-review-hidden\b/g, '').trim();
        (groups[key] = groups[key] || []).push(child);
      });
      Object.keys(groups).forEach(function (key) {
        var list = groups[key];
        if (list.length < 2) return;
        var texty = list.filter(function (node) {
          return (node.textContent || '').replace(/\s+/g, ' ').trim().length >= 12;
        });
        if (texty.length < 2 || texty.length < list.length * 0.6) return;
        var score = texty.length * 1000 + Math.min(999, Math.round(texty.reduce(function (sum, node) { return sum + node.textContent.length; }, 0) / texty.length));
        if (score > bestScore) {
          bestScore = score;
          best = list;
        }
      });
    });
    return best || [];
  }

  function setReviewLabels(toggle, expanded) {
    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    toggle.querySelectorAll('[data-hpdp-reviews-label]').forEach(function (node) {
      node.textContent = expanded ? toggle.dataset.less : node.dataset.more;
    });
  }

  function measureReviews(root) {
    var body = root.querySelector('[data-hpdp-reviews-body]');
    var toggle = root.querySelector('[data-hpdp-reviews-toggle]');
    if (!body || !toggle) return;

    if (!DESKTOP.matches) {
      var items = reviewItems(root, body);
      root.querySelectorAll('.hpdp-review-hidden').forEach(function (node) {
        if (items.indexOf(node) === -1) node.classList.remove('hpdp-review-hidden');
      });
      if (items.length) {
        var expanded = body.dataset.itemsExpanded === 'true';
        body.classList.add('is-itemised');
        body.classList.remove('is-collapsed');
        body.style.maxHeight = '';
        items.forEach(function (node, index) {
          node.classList.toggle('hpdp-review-hidden', index > 0 && !expanded);
        });
        toggle.hidden = items.length < 2;
        setReviewLabels(toggle, expanded && items.length > 1);
        return;
      }
    } else if (body.classList.contains('is-itemised')) {
      body.classList.remove('is-itemised');
      root.querySelectorAll('.hpdp-review-hidden').forEach(function (node) {
        node.classList.remove('hpdp-review-hidden');
      });
      body.dataset.expanded = 'false';
      setReviewLabels(toggle, false);
    }

    body.classList.remove('is-itemised');
    if (body.dataset.expanded === 'true') return;
    body.style.maxHeight = '';
    body.classList.add('is-collapsed');
    var overflowing = body.scrollHeight > body.clientHeight + 8;
    body.classList.toggle('is-collapsed', overflowing);
    toggle.hidden = !overflowing;
  }

  function initReviews(root, signal) {
    var body = root.querySelector('[data-hpdp-reviews-body]');
    var toggle = root.querySelector('[data-hpdp-reviews-toggle]');
    if (body && toggle) {
      toggle.addEventListener(
        'click',
        function () {
          if (body.classList.contains('is-itemised')) {
            var expand = body.dataset.itemsExpanded !== 'true';
            body.dataset.itemsExpanded = expand ? 'true' : 'false';
            measureReviews(root);
            if (!expand) {
              var card = body.closest('.hpdp-card') || body;
              if (card.getBoundingClientRect().top < 0) {
                window.scrollTo({ top: card.getBoundingClientRect().top + window.pageYOffset - headerHeight() - 16, behavior: scrollBehavior() });
              }
            }
            return;
          }
          expandCollapse(body, body, toggle, toggle.getAttribute('aria-expanded') !== 'true', 'is-collapsed');
        },
        { signal: signal }
      );
      var inner = body.querySelector('.hpdp-reviews__inner');
      if (inner && 'ResizeObserver' in window) {
        /* the review app renders asynchronously — re-measure only when its height changes */
        var resize = new ResizeObserver(
          debounce(function () {
            measureReviews(root);
          }, 120)
        );
        resize.observe(inner);
        root._hpdpObservers.push(resize);
      }
      measureReviews(root);
    }

    root.addEventListener(
      'click',
      function (event) {
        var write = event.target.closest('[data-hpdp-write-review]');
        if (!write || !root.contains(write)) return;
        var scope = root.querySelector('[data-hpdp-reviews-body]');
        if (!scope) return;
        var candidates = scope.querySelectorAll('button, a, [role="button"]');
        var match = null;
        Array.prototype.some.call(candidates, function (node) {
          if (/write (a )?review|rate (this|now)|leave (a )?review/i.test(node.textContent || '')) {
            match = node;
            return true;
          }
          return false;
        });
        if (match) {
          match.click();
        } else {
          scrollToTarget(root, scope);
        }
      },
      { signal: signal }
    );
  }

  /* ---------------------------------------------------------------- anchors (rating → reviews, View Similar → slider) */
  function initAnchors(root, signal) {
    root.addEventListener(
      'click',
      function (event) {
        var link = event.target.closest('[data-hpdp-review-link], [data-hpdp-scroll]');
        if (!link || !root.contains(link)) return;
        var target = null;
        if (link.hasAttribute('data-hpdp-review-link')) {
          target = document.getElementById(root.dataset.reviewsAnchor) || document.getElementById('AirReviews-BlockWrapper');
        } else {
          var id = (link.getAttribute('href') || '').replace(/^#/, '');
          target = id ? document.getElementById(id) : null;
        }
        if (!target) return;
        event.preventDefault();
        scrollToTarget(root, target);
        if (window.history && window.history.replaceState) {
          window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search + '#' + target.id);
        }
      },
      { signal: signal }
    );
  }

  /* ---------------------------------------------------------------- offers */
  function initOffers(root, signal) {
    var box = root.querySelector('[data-hpdp-offers]');
    if (!box) return;
    var cards = box.querySelectorAll('.offer-card');
    if (!cards.length) return;

    var link = box.dataset.link;
    var section = box.querySelector('.offers-section') || box;
    if (!box.querySelector('.hpdp-offers__more') && (link || cards.length > 1)) {
      var more;
      if (link) {
        more = document.createElement('a');
        more.href = link;
      } else {
        more = document.createElement('button');
        more.type = 'button';
        more.setAttribute('aria-expanded', 'false');
      }
      more.className = 'hpdp-offers__more';
      more.textContent = 'View more Offers';
      section.appendChild(more);
      if (!link) {
        more.addEventListener(
          'click',
          function () {
            var expanded = box.classList.toggle('is-expanded');
            more.setAttribute('aria-expanded', expanded ? 'true' : 'false');
            more.textContent = expanded ? 'View less' : 'View more Offers';
          },
          { signal: signal }
        );
      }
    }

    /* mobile: every offer card collapses from its header */
    cards.forEach(function (card) {
      var badge = card.querySelector('.offer-badge');
      if (!badge) return;
      badge.setAttribute('role', 'button');
      badge.setAttribute('tabindex', '0');
      badge.setAttribute('aria-expanded', 'true');
      var toggle = function () {
        if (DESKTOP.matches) return;
        var collapsed = card.classList.toggle('is-collapsed');
        badge.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      };
      badge.addEventListener('click', toggle, { signal: signal });
      badge.addEventListener(
        'keydown',
        function (event) {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggle();
          }
        },
        { signal: signal }
      );
    });
  }

  /* ---------------------------------------------------------------- gallery thumb nav (desktop) */
  function initGalleryNav(root, signal) {
    root.addEventListener(
      'click',
      function (event) {
        var button = event.target.closest('[data-hpdp-media]');
        if (!button || !root.contains(button) || !window.mainSplide) return;
        window.mainSplide.go(button.dataset.hpdpMedia === 'prev' ? '<' : '>');
      },
      { signal: signal }
    );
  }

  function ensureGallery(root) {
    var main = root.querySelector('#main-carousel');
    if (!main || main.classList.contains('is-initialized')) return;
    if (typeof window.Splide === 'undefined' || typeof window.initializeSplide !== 'function') return;
    window.initializeSplide();
  }

  /* ---------------------------------------------------------------- Customers Also Viewed (theme Swiper + existing card) */
  function cavGap(cav) {
    var root = cav || document.querySelector('.hpdp');
    var desktopGap = root ? parseFloat(getComputedStyle(root).getPropertyValue('--hpdp-cav-gap')) || 25 : 25;
    return DESKTOP.matches ? desktopGap * unit() : 13.58 * unit();
  }

  function cavConfig(cav) {
    if (cav._hpdpConfig) return cav._hpdpConfig;
    var config = {};
    try {
      config = JSON.parse(cav.getAttribute('data-config') || '{}') || {};
    } catch (error) {
      config = {};
    }
    cav._hpdpConfig = config;
    return config;
  }

  function perView(value, fallback) {
    if (value === 'auto') return 'auto';
    var n = parseFloat(value);
    return n > 0 ? n : fallback;
  }

  /* Moving pagination dots (mobile): the dot row is a small window (`size` dots, e.g. 5 = 2 before + active +
     2 after) that SLIDES along with the slider — all dots stay in the DOM (Swiper/Splide keep working), the row is
     clipped to the window and translated so the active dot stays inside it, clamped at both ends. Dots at the window
     edge are drawn smaller when more dots exist beyond them. Desktop shows the full row. */
  function dotItems(container) {
    var bullets = container.querySelectorAll('.swiper-pagination-bullet');
    if (bullets.length) return Array.prototype.slice.call(bullets);
    return Array.prototype.slice.call(container.querySelectorAll('.splide__pagination__page')).map(function (page) {
      return page.parentElement && page.parentElement.tagName === 'LI' ? page.parentElement : page;
    });
  }

  function windowDots(container, size) {
    if (!container) return;
    var items = dotItems(container);
    var total = items.length;
    if (!total) return;
    var active = -1;
    items.forEach(function (item, index) {
      var dot = item.matches('.swiper-pagination-bullet, .splide__pagination__page') ? item : item.querySelector('.splide__pagination__page') || item;
      if (dot.classList.contains('swiper-pagination-bullet-active') || dot.classList.contains('is-active')) active = index;
    });
    if (active < 0) active = 0;
    var limit = Math.max(1, size || 5);
    var clear = function () {
      container.classList.remove('hpdp-dots-moving');
      container.style.removeProperty('--hpdp-dots-shift');
      container.style.width = '';
      items.forEach(function (item) {
        item.classList.remove('hpdp-dot-edge', 'hpdp-dot-out');
        item.removeAttribute('tabindex');
      });
    };
    if (DESKTOP.matches || total <= limit) {
      clear();
      container.setAttribute('data-dots-window', '1-' + total + '/' + total);
      return;
    }
    var half = Math.floor(limit / 2);
    var start = Math.min(Math.max(0, active - half), total - limit);
    var end = start + limit - 1;
    container.classList.add('hpdp-dots-moving');
    var first = items[0].offsetLeft;
    var left = items[start].offsetLeft - first;
    var right = items[end].offsetLeft + items[end].offsetWidth - first;
    container.style.width = Math.ceil(right - left) + 'px';
    container.style.setProperty('--hpdp-dots-shift', -left + 'px');
    items.forEach(function (item, index) {
      var out = index < start || index > end;
      var edge = (index === start && start > 0) || (index === end && end < total - 1);
      item.classList.toggle('hpdp-dot-out', out);
      item.classList.toggle('hpdp-dot-edge', edge && index !== active);
      if (out) item.setAttribute('tabindex', '-1');
      else item.removeAttribute('tabindex');
    });
    container.setAttribute('data-dots-window', start + 1 + '-' + (end + 1) + '/' + total);
  }

  /* Dots can also be dragged: sliding a finger/mouse along the dot row moves the slider one step per dot. */
  function enableDotDrag(container, step, signal) {
    if (!container || container._hpdpDotDrag) return;
    container._hpdpDotDrag = true;
    var startX = null;
    var moved = false;
    container.addEventListener('pointerdown', function (event) {
      startX = event.clientX;
      moved = false;
      try {
        container.setPointerCapture(event.pointerId);
      } catch (error) {
        /* not supported → plain move events */
      }
    }, { signal: signal });
    container.addEventListener('pointermove', function (event) {
      if (startX === null) return;
      var first = dotItems(container)[0];
      var unit = first ? first.offsetWidth + (parseFloat(getComputedStyle(container).columnGap) || 6) : 13;
      var dx = event.clientX - startX;
      if (Math.abs(dx) >= unit) {
        step(dx < 0 ? 1 : -1);
        startX = event.clientX;
        moved = true;
      }
    }, { signal: signal });
    var end = function () {
      startX = null;
    };
    container.addEventListener('pointerup', end, { signal: signal });
    container.addEventListener('pointercancel', end, { signal: signal });
    /* a drag must not also count as a dot click */
    container.addEventListener('click', function (event) {
      if (moved) {
        event.preventDefault();
        event.stopPropagation();
        moved = false;
      }
    }, { capture: true, signal: signal });
  }

  function buildSlider(root) {
    var cav = root.querySelector('[data-hpdp-cav]');
    if (!cav) return;
    if (root._hpdpSwiper) {
      root._hpdpSwiper.destroy(true, true);
      root._hpdpSwiper = null;
    }
    var config = cavConfig(cav);
    var slider = cav.querySelector('[data-hpdp-cav-slider]');
    var count = slider ? slider.querySelectorAll('.swiper-slide').length : 0;
    cav.classList.toggle('is-empty', count === 0);
    var dots = cav.querySelector('[data-hpdp-cav-dots]');
    if (dots) dots.innerHTML = '';
    if (!slider || !count || typeof window.Swiper === 'undefined' || config.slider === false) return;
    var mobileGap = config.mGap !== undefined ? config.mGap : 13.58 * unit();
    var options = {
      slidesPerView: perView(config.mVisible, 2),
      slidesPerGroup: 1,
      spaceBetween: mobileGap,
      speed: config.speed || 400,
      loop: !!config.loop && count > 2,
      watchOverflow: true,
      grabCursor: config.drag !== false,
      simulateTouch: config.drag !== false,
      allowTouchMove: config.swipe !== false,
      a11y: { enabled: true },
      navigation: {
        prevEl: cav.querySelector('[data-hpdp-cav-prev]'),
        nextEl: cav.querySelector('[data-hpdp-cav-next]')
      },
      breakpoints: {
        600: { slidesPerView: perView(config.tVisible, 3), spaceBetween: config.tGap !== undefined ? config.tGap : 14 },
        1024: { slidesPerView: perView(config.dVisible, 'auto'), slidesPerGroup: config.dGroup || 1, spaceBetween: cavGap(cav) }
      },
      on: {
        init: function () { windowDots(dots, config.dotWindow); },
        paginationUpdate: function () { windowDots(dots, config.dotWindow); },
        slideChange: function () { windowDots(dots, config.dotWindow); },
        breakpoint: function () { windowDots(dots, config.dotWindow); }
      }
    };
    if (dots) {
      options.pagination = { el: dots, clickable: true, bulletElement: 'button' };
    }
    if (config.autoplay) {
      options.autoplay = { delay: config.autoplayMs || 4000, disableOnInteraction: false, pauseOnMouseEnter: true };
    }
    var swiper = new window.Swiper(slider, options);
    root._hpdpSwiper = swiper;
    windowDots(dots, config.dotWindow);
    enableDotDrag(dots, function (dir) {
      var current = root._hpdpSwiper;
      if (!current || current.destroyed) return;
      if (dir > 0) current.slideNext();
      else current.slidePrev();
    }, root._hpdpController ? root._hpdpController.signal : undefined);
  }

  function refreshSlider(root) {
    var swiper = root._hpdpSwiper;
    if (!swiper || swiper.destroyed) return;
    var cav = root.querySelector('[data-hpdp-cav]');
    if (swiper.params.breakpoints && swiper.params.breakpoints[1024]) swiper.params.breakpoints[1024].spaceBetween = cavGap(cav);
    swiper.update();
    if (cav) windowDots(cav.querySelector('[data-hpdp-cav-dots]'), cavConfig(cav).dotWindow);
  }

  /* Main gallery (Splide, product-media.js) pagination — mobile only (Splide shows it ≤600px).
     Only a window of `size` dots is shown: active + 2 before + 2 after, clamped at both ends; ≤ size dots → all.
     Dots outside the window get .is-dot-hidden (removed from layout, still in the DOM). The pagination stays
     visibility:hidden (hok-pdp.css) until the window has been applied (.is-pagination-ready), so the full row never
     flashes on load. product-media.js creates window.mainSplide on DOMContentLoaded (after this deferred script) and
     re-creates it on every shade change, so a watcher on #main-carousel re-applies the window to every new pagination
     list before it is painted. Product-slider dots keep using windowDots(). */
  function windowGalleryDots(pager, size) {
    if (!pager) return;
    var items = Array.prototype.slice.call(pager.children);
    var total = items.length;
    var limit = Math.max(1, size || 5);
    var active = 0;
    items.forEach(function (item, index) {
      if (item.querySelector('.is-active') || item.classList.contains('is-active')) active = index;
    });
    var sliding = total > limit;
    var start = 0;
    var end = total - 1;
    if (sliding) {
      start = Math.min(Math.max(0, active - Math.floor(limit / 2)), total - limit);
      end = start + limit - 1;
    }
    /* Sliding window: the <ul> is sized to exactly `limit` dots (overflow: hidden) and every dot is shifted with
       transform: translateX(--hpdp-dot-shift), so the row glides along as the active dot moves. Offsets are read
       from offsetLeft/offsetWidth, which ignore transforms; plain transform + overflow (no clip-path / `translate`
       property) keeps it working the same on iPhone Safari and Android Chrome. */
    if (sliding) {
      if (!items[end].offsetWidth) return; /* not laid out yet — re-run on the next pagination/mount event */
      var first = items[0].offsetLeft;
      var left = items[start].offsetLeft - first;
      var right = items[end].offsetLeft + items[end].offsetWidth - first;
      pager.classList.add('is-dots-sliding');
      pager.style.width = Math.ceil(right - left) + 'px';
      pager.style.setProperty('--hpdp-dot-shift', -left + 'px');
    } else {
      pager.classList.remove('is-dots-sliding');
      pager.style.width = '';
      pager.style.removeProperty('--hpdp-dot-shift');
    }
    items.forEach(function (item, index) {
      var hidden = index < start || index > end;
      item.classList.toggle('is-dot-hidden', hidden);
      item.classList.toggle('is-dot-edge', sliding && index !== active && ((index === start && start > 0) || (index === end && end < total - 1)));
      var button = item.querySelector('button');
      if (button) {
        if (hidden) {
          button.setAttribute('tabindex', '-1');
          button.setAttribute('aria-hidden', 'true');
        } else {
          button.removeAttribute('tabindex');
          button.removeAttribute('aria-hidden');
        }
      }
    });
    pager.setAttribute('data-dots-window', total ? start + 1 + '-' + (end + 1) + '/' + total : '0/0');
    /* reveal only after the window is applied (first paint shows the correct 5); the slide transition is enabled
       one frame later so the very first placement never animates */
    if (!pager.classList.contains('is-pagination-ready')) {
      pager.classList.add('is-pagination-ready');
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          pager.classList.add('is-dots-animated');
        });
      });
    }
  }

  function initGalleryDots(root) {
    var carousel = root.querySelector('#main-carousel');
    if (!carousel) return; /* only the PDP root that owns the gallery */
    var cav = root.querySelector('[data-hpdp-cav]');
    var size = cav ? cavConfig(cav).dotWindow : 5;
    var run = function () {
      var pager = carousel.querySelector('.splide__pagination');
      if (!pager) return;
      windowGalleryDots(pager, size);
      if (!pager._hpdpDotWatcher) {
        pager._hpdpDotWatcher = new MutationObserver(run);
        pager._hpdpDotWatcher.observe(pager, { childList: true });
      }
      if (!pager._hpdpDotDragBound) {
        pager._hpdpDotDragBound = true;
        enableDotDrag(pager, function (dir) {
          if (window.mainSplide) window.mainSplide.go(dir > 0 ? '>' : '<');
        });
      }
    };
    var attach = function () {
      var splide = window.mainSplide;
      if (splide && !splide._hpdpDots && typeof splide.on === 'function') {
        splide._hpdpDots = true;
        splide.on('mounted move moved pagination:mounted pagination:updated resized', run);
      }
      run();
    };
    attach();
    root._hpdpAttachGalleryDots = attach;
    var watcher = new MutationObserver(attach);
    watcher.observe(carousel, { childList: true, subtree: true });
    root._hpdpObservers.push(watcher);
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', attach, { once: true });
    }
  }

  /* Desktop sticky side card: its shade dots are display-only. Clicking them scrolls to the MAIN shade selector
     (.hpdp-variants) so the shade is picked there; nothing is selected from the side card. */
  function initCardShadeJump(root, signal) {
    var dots = root.querySelector('.hpdp__aside [data-hpdp-card-dots]');
    var target = root.querySelector('.hpdp__info .hpdp-variants') || root.querySelector('.hpdp-variants');
    if (!dots || !target) return;
    dots.removeAttribute('aria-hidden');
    dots.setAttribute('role', 'button');
    dots.setAttribute('tabindex', '0');
    dots.setAttribute('aria-label', 'Choose a shade — go to the shade selector');
    dots.classList.add('hpdp-card-dots--jump');
    var jump = function (event) {
      if (!DESKTOP.matches) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      var current = target.querySelector('input[type="radio"]:checked') || target.querySelector('input[type="radio"]');
      if (current) {
        try {
          current.focus({ preventScroll: true });
        } catch (error) {
          /* focus options unsupported: skip focusing so the smooth scroll isn't interrupted */
        }
      }
    };
    dots.addEventListener('click', jump, { signal: signal });
    dots.addEventListener('keydown', function (event) {
      if (event.key === 'Enter' || event.key === ' ') jump(event);
    }, { signal: signal });
  }

  function initCav(root) {
    var cav = root.querySelector('[data-hpdp-cav]');
    if (!cav) return;
    buildSlider(root);
    var recs = cav.querySelector('product-recommendations');
    if (!recs) return;
    /* global.js <product-recommendations> swaps in Shopify recommendations once; rebuild the slider then */
    var observer = new MutationObserver(function () {
      observer.disconnect();
      buildSlider(root);
    });
    observer.observe(recs, { childList: true });
    root._hpdpObservers.push(observer);
  }

  /* ---------------------------------------------------------------- lifecycle */
  function initRoot(root) {
    if (!root || root.dataset.hpdpReady === 'true') return;
    root.dataset.hpdpReady = 'true';
    var controller = new AbortController();
    var signal = controller.signal;
    root._hpdpController = controller;
    root._hpdpObservers = [];

    setOffsets(root);
    initAnchors(root, signal);
    initTabs(root, signal);
    initReadMore(root, signal);
    initReviews(root, signal);
    initVariantSync(root, signal);
    initBuyNow(root, signal);
    initProxyButtons(root, signal);
    initOffers(root, signal);
    initGalleryNav(root, signal);
    initMobileBar(root);
    initCav(root);
    initGalleryDots(root);
    initCardShadeJump(root, signal);
    window.addEventListener('load', function () { if (root._hpdpAttachGalleryDots) root._hpdpAttachGalleryDots(); }, { signal: signal });

    var onResize = debounce(function () {
      setOffsets(root);
      refreshReadMore(root);
      measureReviews(root);
      refreshSlider(root);
      if (root._hpdpBarRender) root._hpdpBarRender();
    }, 150);
    window.addEventListener('resize', onResize, { signal: signal });
    DESKTOP.addEventListener('change', onResize, { signal: signal });

    if (window.Shopify && window.Shopify.designMode) ensureGallery(root);
  }

  function destroyRoot(root) {
    if (!root || !root._hpdpController) return;
    root._hpdpController.abort();
    root._hpdpObservers.forEach(function (observer) {
      observer.disconnect();
    });
    root._hpdpObservers = [];
    if (root._hpdpSwiper) {
      root._hpdpSwiper.destroy(true, true);
      root._hpdpSwiper = null;
    }
    root.dataset.hpdpReady = 'false';
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('.hpdp').forEach(initRoot);
  }

  window.HokPDP = { initAll: initAll, buildSlider: buildSlider };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  window.addEventListener('load', function () {
    document.querySelectorAll('.hpdp').forEach(function (root) {
      setOffsets(root);
      refreshReadMore(root);
      measureReviews(root);
    });
  });

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('.hpdp').forEach(destroyRoot);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var tab = document.getElementById('HpdpTab-' + event.detail.blockId);
    if (!tab) return;
    var root = tab.closest('.hpdp');
    if (!root) return;
    if (DESKTOP.matches) {
      selectTab(root, tab, false);
    } else {
      var panel = document.getElementById(tab.getAttribute('aria-controls'));
      if (panel) setAccordion(panel, true);
    }
  });
})();
